import React from 'react';
import { useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { Expandable, Grid, Typography, units } from '@hbf/dsl';
import { makeStyles } from 'tss-react/mui';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';

import { getListingIsCharitable } from '../selectors';

const useStyles = makeStyles()(theme => ({
  wrapper: {
    marginBottom: theme.spacing(2),
  },
  ol: {
    paddingInlineStart: units.rem(1),
  },
}));

interface Props {
  countryCode: string;
}

const FaqSectionInternal: React.FC<Props> = ({ countryCode }) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const trackEvent = useTrackEvent();

  const isCharitable = useSelector(getListingIsCharitable);

  const questions = React.useMemo(() => {
    if (isCharitable) {
      return [
        {
          question: T('ldp.faq_ukr.how_to_book_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.how_to_book_answer')}</p>
            </div>
          ),
        },
        {
          question: T('ldp.faq_ukr.fees_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.fees_answer')}</p>
            </div>
          ),
        },
        {
          question: T('ldp.faq_ukr.who_can_book_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.who_can_book_answer')}</p>
            </div>
          ),
        },
        {
          question: T('ldp.faq_ukr.unsure_duration_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.unsure_duration_answer')}</p>
            </div>
          ),
        },
        {
          question: T('ldp.faq_ukr.early_moveout_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.early_moveout_answer')}</p>
            </div>
          ),
        },
        {
          question: T('ldp.faq_ukr.screening_tips_question'),
          answer: (
            <div>
              <p>{T('ldp.faq_ukr.screening_tips_answer_intro')}</p>
              <div>
                <ol className={classes.ol}>
                  <li>
                    {T('ldp.faq_ukr.screening_tips_answer_tip1_too_good')}
                  </li>
                  <li>
                    {T('ldp.faq_ukr.screening_tips_answer_tip2_ask_questions')}
                  </li>
                  <li>
                    {T('ldp.faq_ukr.screening_tips_answer_tip3_pay_on_HA')}
                  </li>
                </ol>
              </div>
              <div>
                <p>{T('ldp.faq_ukr.screening_tips_answer_rent_safeguard')}</p>
              </div>
            </div>
          ),
        },
      ];
    }

    return [
      {
        question: T('ldp.faq.question_1'),
        answer: (
          <div>
            <p>{T('ldp.faq.answer_1')}</p>
            <div>
              <ol className={classes.ol}>
                <li>{T('ldp.faq.answer_11')}</li>
                {countryCode === 'nl' && <li>{T('ldp.faq.answer_11_b')}</li>}
                <li>{T('ldp.faq.answer_12')}</li>
                <li>{T('ldp.faq.answer_13')}</li>
                <li>{T('ldp.faq.answer_14')}</li>
                <li>{T('ldp.faq.answer_15')}</li>
              </ol>
            </div>
          </div>
        ),
      },
      {
        question: T('ldp.faq.question_2'),
        answer: (
          <div>
            <p>{T('ldp.faq.answer_2')}</p>
            <div>
              <ol className={classes.ol}>
                <li>{T('ldp.faq.answer_21')}</li>
                <li>{T('ldp.faq.answer_22')}</li>
                <li>{T('ldp.faq.answer_23')}</li>
              </ol>
            </div>
          </div>
        ),
      },
      { question: T('ldp.faq.question_3'), answer: T('ldp.faq.answer_3') },
    ];
  }, [T, isCharitable, countryCode, classes.ol]);

  const handleChange = React.useCallback(
    (faqQuestion: number) => (expanded: boolean) => {
      if (expanded) {
        trackEvent('Clicked LDP FAQ', { faqQuestion });
      }
    },
    [trackEvent],
  );

  return (
    <Grid
      item
      container
      xs={12}
      spacing={1}
      className={classes.wrapper}
      id="faq"
    >
      <Grid item xs={12}>
        <Typography.H3>{T('ldp.faq.title')}</Typography.H3>
      </Grid>
      <Grid item xs={12}>
        {questions.map(({ question, answer }, i) => (
          <Expandable
            key={i}
            summary={question}
            details={answer}
            onChange={handleChange(i + 1)}
          />
        ))}
      </Grid>
    </Grid>
  );
};

const FaqSection = React.memo(FaqSectionInternal);
export { FaqSection };
